import { ExpandLess, ExpandMore } from '@mui/icons-material'
import {
    Box,
    Chip,
    Collapse,
    IconButton,
    Stack,
    TableCell,
    TableRow,
    Typography
} from '@mui/material'
import React, { useState } from 'react'
import { RaRecord, useRedirect, useTranslate } from 'react-admin'
import { extractActionTypes, extractRuleInputs } from '../utils/parseRuleData'

interface ExpandableDatagridRowProps {
    record: RaRecord
    builderConfig: any
    children: React.ReactNode
}

const ExpandableDatagridRow: React.FC<ExpandableDatagridRowProps> = ({
    record,
    builderConfig,
    children
}) => {
    const [expanded, setExpanded] = useState(false)
    const translate = useTranslate()
    const redirect = useRedirect()

    const inputs = extractRuleInputs(record.rules || '{}', builderConfig)
    const actions = extractActionTypes(record.actions || '{}')

    const handleExpandClick = (event: React.MouseEvent) => {
        event.stopPropagation() // Prevent row click from triggering
        setExpanded(!expanded)
    }

    const handleRowClick = () => {
        redirect('edit', '/Rule', record.id)
    }

    // Always allow expansion since we now show labels even for empty data
    const needsExpansion = true

    return (
        <>
            <TableRow
                hover
                onClick={handleRowClick}
                sx={{
                    cursor: 'pointer',
                    height: '42px !important',
                    maxHeight: '42px',
                    '& .MuiTableCell-root': {
                        paddingTop: '0px',
                        paddingBottom: '0px',
                        height: '42px !important',
                        maxHeight: '42px',
                        lineHeight: '1.2',
                        '& > *': {
                            fontSize: '0.875rem',
                            lineHeight: '1.2'
                        }
                    }
                }}
            >
                <TableCell padding="checkbox">
                    {needsExpansion && (
                        <IconButton
                            size="small"
                            onClick={handleExpandClick}
                            aria-expanded={expanded}
                            aria-label="expand row"
                            sx={{
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    transform: 'scale(1.05)'
                                }
                            }}
                        >
                            {expanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    )}
                </TableCell>
                {children}
            </TableRow>

            {needsExpansion && (
                <TableRow>
                    <TableCell
                        style={{
                            paddingBottom: 0,
                            paddingTop: 0,
                            borderTop: expanded ? 'none' : undefined
                        }}
                        colSpan={7}
                    >
                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2 }}>
                                <Typography variant="h6" gutterBottom component="div">
                                    {translate('manager.resources.rule.rule_details')}
                                </Typography>

                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        {translate('manager.resources.rule.rule_inputs')}:
                                    </Typography>
                                    {inputs.length > 0 ? (
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            flexWrap="wrap"
                                            useFlexGap
                                        >
                                            {inputs.map((input, index) => (
                                                <Chip
                                                    key={index}
                                                    label={input}
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                />
                                            ))}
                                        </Stack>
                                    ) : (
                                        <Chip
                                            label={translate('manager.resources.rule.always_apply')}
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                        />
                                    )}
                                </Box>

                                <Box sx={{ mb: 1 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        {translate('manager.resources.rule.actions_applied')}:
                                    </Typography>
                                    {actions.length > 0 ? (
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            flexWrap="wrap"
                                            useFlexGap
                                        >
                                            {actions.map((action, index) => (
                                                <Chip
                                                    key={index}
                                                    label={action}
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                />
                                            ))}
                                        </Stack>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            —
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}
        </>
    )
}

export default ExpandableDatagridRow
